import React from 'react'
import LocalHelmet from '../common/LocalHelmet'
import { motion } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiShield, FiEye, FiLock, FiGlobe, FiMail, FiCalendar } = FiIcons

const Privacy = () => {
  const lastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  const sections = [
    {
      id: 'information-we-collect',
      title: 'Information We Collect',
      icon: FiEye,
      content: (
        <div className="space-y-4">
          <p className="text-secondary-600 leading-relaxed">
            We collect information to provide better services to our users and improve our website experience.
          </p>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-secondary-900 mb-2">Information You Provide Directly</h4>
              <ul className="list-disc list-inside space-y-1 text-secondary-600 ml-4">
                <li>Name, email address, and company information from contact forms</li>
                <li>Project details, budget information, and service interests</li>
                <li>Messages and communications sent through our website</li>
                <li>Meeting scheduling information through Google Calendar integration</li>
                <li>Newsletter subscription preferences</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'sharing-information',
      title: 'Sharing of Information',
      icon: FiShield,
      content: (
        <div className="space-y-4">
          <p className="text-secondary-600 leading-relaxed font-medium">
            We do not sell, rent, or trade your personal data to third parties.
          </p>
          
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold text-secondary-900 mb-2">Service Providers</h4>
              <p className="text-secondary-600 text-sm">
                We work with trusted service providers who help us operate our website:
              </p>
              <ul className="list-disc list-inside space-y-1 text-secondary-600 ml-4 text-sm mt-2">
                <li>Supabase (database services)</li>
                <li>Google Calendar (meeting scheduling)</li>
                <li>Analytics providers (Plausible, etc.)</li>
                <li>Hosting and CDN services</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ]

  return (
    <>
      <LocalHelmet prioritizeSeoTags>
        <title>Privacy Policy | Richardson Strategy Group</title>
        <meta name="description" content="Read Richardson Strategy Group's privacy policy. See how we protect your information." />
        <link rel="canonical" href="https://richardsonstrategy.group/privacy" />
      </LocalHelmet>

      <div className="min-h-screen py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center justify-center space-x-3 mb-6"
            >
              <SafeIcon icon={FiShield} className="w-8 h-8 text-primary-600" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary-900">Privacy Policy</h1>
            </motion.div>
            <p className="text-lg text-secondary-600 mb-4">Last updated: {lastUpdated}</p>
          </div>

          <div className="space-y-12">
            {sections.map((section) => (
              <section key={section.id} id={section.id}>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={section.icon} className="w-5 h-5 text-primary-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-secondary-900">{section.title}</h2>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-secondary-100">
                  {section.content}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

export default Privacy